import { useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery } from 'hooks/useCustomQuery';

import { multiSelectDataMapper } from 'utils/utils';
import { axios } from 'services/axios';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from 'components/v4';

import FormBase from '@/components/forms/new/FormBase';

// Form field components
import DirectRoutingRegionsField from '../serviceprofiles/edit/formFields/DirectRoutingRegionsField';
import DirectRoutingVoicePoliciesField from '../serviceprofiles/edit/formFields/DirectRoutingVoicePoliciesField';
import DirectRoutingDialPlansField from '../serviceprofiles/edit/formFields/DirectRoutingDialPlansField';

const DEFAULT_DATA = {
    name: 'Direct Routing GCCH',
    regions: [],
    voicePolicies: [],
    dialPlans: [],
    visibleScope: 0,
};

/** Direct Routing component for Edit Services */
export default function GCCHEditService({ mode }) {
    const { id: serviceId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);



    const { currentCompany: companyID } = useSelector(
        (state) => state.navigationLists,
    );
    const { darkMode } = useSelector((state) => state.settings);
    const { state } = useLocation();

    const handleBackButton = () => navigate('/dashboard-services');

    // Initial data loading
    const [initialValues, setInitialValues] = useState(DEFAULT_DATA);

    // Load service data for edit mode
    const { isLoading: isLoadingService } = useQuery({
        queryKey: ['directRoutingGCCHData', serviceId],
        queryFn: async () => {
            const res = await axios.get(`/Service/${serviceId}`);
            return res.data;
        },
        onSuccess: async (data) => {
            const voicePolicies =
                data?.voicePolicies?.map(multiSelectDataMapper) || [];
            const regions = data?.regions?.map(multiSelectDataMapper) || [];
            const dialPlans = data?.dialPlans?.map(multiSelectDataMapper) || [];

            setInitialValues({
                ...data,
                voicePolicies,
                regions,
                dialPlans,
                visibleScope: data.visibleScope || 0,
            });
        },
        // enabled: !!serviceId && mode === 'edit' && !state,
        refetchOnWindowFocus: false,
    });

    // Track current regions for voice policies loading
    const [currentRegions, setCurrentRegions] = useState([]);

    const regionIds = currentRegions.map((v) => v.id);

    const { data: voicePolicies = [] } = useQuery({
        queryKey: ['drGCCHVoicePolicies', companyID, regionIds],
        queryFn: async () => {
            const regionIDQuery = new URLSearchParams({
                RegionIDs: regionIds.join(','),
            });
            const endpoint = `/Services/DirectRoutingGCCH/Company/${companyID}/VoicePolicies/Available?${regionIDQuery}`;
            const result = await axios.get(endpoint);
            return result.data.map(multiSelectDataMapper);
        },
        enabled: currentRegions.length > 0 && Boolean(companyID),
    });

    const { data: dialPlans = [] } = useQuery({
        queryKey: ['drGCCHDialPlans', companyID],
        queryFn: async () => {
            const result = await axios.get(
                `/Services/DirectRoutingGCCH/Company/${companyID}/DialPlans/Available`,
            );
            return result.data.map(multiSelectDataMapper);
        },
        enabled: Boolean(companyID),
    });

    const { data: regions = [] } = useQuery({
        queryKey: ['drGCCHRegions', companyID],
        queryFn: async () => {
            const result = await axios.get(
                `/Services/DirectRoutingGCCH/Company/${companyID}/Regions/Available`,
            );
            return result.data.map(multiSelectDataMapper);
        },
        enabled: Boolean(companyID),
    });

    useEffect(() => {
        if (state?.id) {
            const voicePolicies =
                state?.voicePolicies?.map(multiSelectDataMapper) || [];
            const regions = state?.regions?.map(multiSelectDataMapper) || [];
            const dialPlans =
                state?.dialPlans?.map(multiSelectDataMapper) || [];

            setInitialValues({
                ...state,
                voicePolicies,
                regions,
                dialPlans,
                visibleScope: state.visibleScope || 0,
            });
            setCurrentRegions(regions);
        }
    }, [state]);

    // Sync currentRegions when initialValues.regions changes (from useQuery)
    useEffect(() => {
        if (initialValues.regions && initialValues.regions.length > 0) {
            setCurrentRegions(initialValues.regions);
        }
    }, [initialValues.regions]);

    const handleSubmit = async (values) => {
        const payload = {
            ...values,
            companyID,
        };

        setIsLoading(true);
        try {
            const endpoint = '/Service/DirectRoutingGCCH';

            switch (mode) {
                case 'add':
                    await axios.post(endpoint, payload);
                    toast({
                        status: 'success',
                        title: 'Service Added',
                        description: 'Direct Routing service has been created.',
                        isClosable: true,
                        duration: 3000,
                    });
                    break;
                case 'edit':
                    await axios.put(endpoint, payload);
                    toast({
                        status: 'success',
                        title: 'Service Updated',
                        description: 'Direct Routing GCCH has been updated.',
                        isClosable: true,
                        duration: 3000,
                    });
                    break;
            }
            navigate('/dashboard-services');
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        {
            label: `${mode === 'add' ? 'Add' : 'Edit'} Direct Routing GCCH Service`,
            path: '',
        },
    ];

    // Only show loading spinner for edit mode when actually loading service data
    if (mode === 'edit' && isLoadingService && !state) {
        return <LoadingIndicator />;
    }

    return (
        <FormBase
            breadCrumbTrail={breadCrumbTrail}
            header={`${mode === 'add' ? 'Add' : 'Editing'} Direct Routing GCCH`}
            initialValues={initialValues}
            finalFormSubmit={handleSubmit}
            customClick={true}
            onBack={handleBackButton}
            enableReinitialize={true}
            isSubmitDisabled={isLoading}>
            {({ values, setFieldValue, errors, touched }) =>
                [
                    <DirectRoutingRegionsField
                        key="regions"
                        value={values.regions}
                        onChange={(selectedOptions) => {
                            setFieldValue('regions', selectedOptions);
                            setCurrentRegions(selectedOptions);
                            
                            // Filter voice policies - keep only those that are still valid
                            if (values.voicePolicies && values.voicePolicies.length > 0) {
                                // Voice policies will be filtered after new options load
                                // We'll update this in a useEffect watching voicePolicies options
                            }
                        }}
                        options={regions}
                        darkMode={darkMode}
                        error={errors.regions}
                        showError={touched.regions}
                    />,
                    <DirectRoutingVoicePoliciesField
                        key="voicePolicies"
                        value={values.voicePolicies}
                        onChange={(selectedOptions) =>
                            setFieldValue('voicePolicies', selectedOptions)
                        }
                        options={voicePolicies}
                        darkMode={darkMode}
                        error={errors.voicePolicies}
                        showError={touched.voicePolicies}
                    />,
                    <DirectRoutingDialPlansField
                        key="dialPlans"
                        value={values.dialPlans}
                        onChange={(selectedOptions) =>
                            setFieldValue('dialPlans', selectedOptions)
                        }
                        options={dialPlans}
                        darkMode={darkMode}
                        error={errors.dialPlans}
                        showError={touched.dialPlans}
                    />,
                    // <DirectRoutingVisibleScopeField
                    //     key="visibleScope"
                    //     field={{ name: 'visibleScope', value: values.visibleScope }}
                    //     form={{ values, setFieldValue, errors, touched }}
                    //     options={[
                    //         { value: 0, label: 'Global' },
                    //         { value: 1, label: 'Company' },
                    //         { value: 2, label: 'Department' },
                    //     ]}
                    //     darkMode={darkMode}
                    // />,
                    error && (
                        <Alert
                            key="error"
                            status="error"
                            sx={{ marginTop: '1rem' }}>
                            <AlertIcon />
                            <AlertDescription>
                                {error?.response?.data?.message ||
                                    'An error occured. please try again later.'}
                            </AlertDescription>
                        </Alert>
                    ),
                ].filter(Boolean)
            }
        </FormBase>
    );
}
